import {
  getResolvedSchemaNodeAtPath,
  resolveSchemaPropertyLabel,
} from '@web-config-app/schema-utils';
import { findClosestAncestorPath } from '@web-config-app/core';
import type { TreeNode } from '@web-config-app/core';
import { get } from 'lodash-es';

export interface GetBreadcrumbsForPropertyProps {
  path: string;
  data: unknown;
  pathPrefix?: string;
  entityTreeNodeMap: Map<string, TreeNode>;
  translate: (args: { id: string }) => string;
}

interface Breadcrumb {
  label: string;
  path: string;
  nodeId: string;
}

/**
 * Formats translation paths into breadcrumbs with proper locale handling
 * and entity schema-based labeling
 */
const formatTranslationPathToBreadcrumbs = (
  path: string,
  entityTreeNodeMap: Map<string, TreeNode>,
  data: unknown,
  pathPrefix: string,
  translate: (args: { id: string }) => string,
): Breadcrumb[] => {
  // Extract everything after "translations" (handles both start and middle positions)
  const translationsMatch = path.match(/translations\.(.+)/);
  if (!translationsMatch) {
    return [
      { label: 'Translation', path: 'translations', nodeId: 'translations' },
    ];
  }

  const remainingPath = translationsMatch[1];

  // Extract locale from bracket notation ["fr-CA"] or simple format fr-CA
  const localeMatch = remainingPath.match(
    /^(?:\[["']([^"']+)["']\]|([^.]+))\.?(.*)$/,
  );
  if (!localeMatch) {
    return [
      { label: 'Translation', path: 'translations', nodeId: 'translations' },
    ];
  }

  const locale = localeMatch[1] || localeMatch[2];
  const fieldPath = localeMatch[3];

  const breadcrumbs: Breadcrumb[] = [];

  if (locale) {
    breadcrumbs.push({
      label: `Translation (${locale})`,
      path: `translations.${locale}`,
      nodeId: `translations.${locale}`,
    });
  } else {
    breadcrumbs.push({
      label: 'Translation',
      path: 'translations',
      nodeId: 'translations',
    });
  }

  if (fieldPath) {
    const segments = fieldPath
      .split('.')
      .filter(
        (segment) =>
          !['domainEntityAttributes', 'entityMetadata'].includes(segment),
      );
    const equivalentPath = segments.join('.');

    if (equivalentPath) {
      let builtPath = '';
      const fieldBreadcrumbs = equivalentPath
        .split('.')
        .filter(
          (key) =>
            ![
              'attributes',
              'domainEntityAttributes',
              'entityMetadata',
            ].includes(key),
        )
        .reduce((acc: Array<Breadcrumb>, key: string) => {
          if (builtPath) {
            builtPath += `.${key}`;
          } else {
            builtPath = key;
          }

          const nodeId = findClosestAncestorPath(builtPath, (candidate) =>
            entityTreeNodeMap.has(candidate),
          );
          const node = nodeId && entityTreeNodeMap.get(nodeId);
          const isArrayItem = !Number.isNaN(parseInt(key, 10));

          if (node) {
            if (builtPath === nodeId && node.name) {
              const label = isArrayItem
                ? `${node.name} (${parseInt(key, 10) + 1})`
                : node.name;
              return [...acc, { label, path: builtPath, nodeId }];
            }

            const nodeSchemaPath = builtPath.replace(`${nodeId}.`, '');
            const pathData = get(data, `${pathPrefix}.${builtPath}`);

            const propertySchema = getResolvedSchemaNodeAtPath(
              nodeSchemaPath,
              node.data?.schema ?? {},
              pathData,
            );

            const labelText = resolveSchemaPropertyLabel(
              propertySchema,
              translate,
            );
            if (labelText) {
              const label = isArrayItem
                ? `${labelText} (${parseInt(key, 10) + 1})`
                : labelText;
              return [...acc, { label, path: builtPath, nodeId }];
            }
          }
          return acc;
        }, []);

      // Add the field breadcrumbs to our translation breadcrumbs
      breadcrumbs.push(...fieldBreadcrumbs);
    }
  }

  return breadcrumbs;
};

export const getBreadcrumbsForProperty = ({
  entityTreeNodeMap,
  path,
  data,
  pathPrefix = '',
  translate = ({ id }) => id,
}: GetBreadcrumbsForPropertyProps): Breadcrumb[] => {
  // Handle translation paths with special formatting
  if (path.startsWith('translations.') || path.includes('.translations.')) {
    return formatTranslationPathToBreadcrumbs(
      path,
      entityTreeNodeMap,
      data,
      pathPrefix,
      translate,
    );
  }

  let builtPath = '';
  const breadcrumbs = path
    .split('.')
    .filter(
      (key) =>
        !['attributes', 'domainEntityAttributes', 'entityMetadata'].includes(
          key,
        ),
    )
    .reduce((acc: Array<Breadcrumb>, key: string) => {
      if (builtPath) {
        builtPath += `.${key}`;
      } else {
        builtPath = key;
      }

      const nodeId = findClosestAncestorPath(builtPath, (candidate) =>
        entityTreeNodeMap.has(candidate),
      );
      const node = nodeId && entityTreeNodeMap.get(nodeId);
      const isArrayItem = !Number.isNaN(parseInt(key, 10));

      if (node) {
        /* Exact match of a tree node path, use the tree node name */
        if (builtPath === nodeId && node.name) {
          const label = isArrayItem
            ? `${node.name} (${parseInt(key, 10) + 1})`
            : node.name;
          return [...acc, { label, path: builtPath, nodeId }];
        }

        /**
         * There was no exact match so we're now operating on some property that is part
         * of the tree node's schema.
         */
        const nodeSchemaPath = builtPath.replace(`${nodeId}.`, '');
        const pathData = get(data, `${pathPrefix}.${builtPath}`);

        /**
         * This grabs the schema for the property. It will resolve any
         * oneOf schemas via the discriminator property if it exists and
         * the data provides the discriminator value.
         */
        const propertySchema = getResolvedSchemaNodeAtPath(
          nodeSchemaPath,
          node.data?.schema ?? {},
          pathData,
        );

        const labelText = resolveSchemaPropertyLabel(propertySchema, translate);
        if (labelText) {
          const label = isArrayItem
            ? `${labelText} (${parseInt(key, 10) + 1})`
            : labelText;
          return [...acc, { label, path: builtPath, nodeId }];
        }
      }
      return acc;
    }, []);

  return breadcrumbs;
};
